/* eslint-disable max-lines */
import { startOfDay } from 'date-fns';
import React, {Children, useEffect, useState} from 'react';
import {
  HSDrug,
  SyringeDriverRunActivityDto, SyringeDriverRunDto, SyringeDriverRunScheduledActivityDto, SyringeDriverRunStatusDto
} from 'server-openapi';
import styled, {useTheme} from 'styled-components';
import {useMap} from '../../../../core/storage/hooks/UseStore';
import { DateUtils } from '../../../../core/utils/dateUtils';
import { Button } from '../../../../kit/Button';
import { Layout } from '../../../../kit/Layout';
import { Text } from '../../../../kit/Text';
import { useSyncCenter } from '../../../../syncstream/SyncCenterProvider';
import { useApiUtils } from '../../../../syncstream/utils/hooks/useApiUtils';
import {MedicationInformationProps} from './MedicationInformationBox';
import { toasts } from '../../../../kit/Toasts/Toaster';
import {
  SyringeDriverRunActivityTypeDto,
  SyringeDriverRunScheduledActivityStatusDto
} from "server-openapi/dist/generated-sdk/api";
import {SyringeDriverRunActivityDialog} from "../syringeDrivers/SyringeDriverRunActivityForm";

interface SdrActivity {
  activityTime: string;
  activityType: "Scheduled" | "Performed";
  scheduledActivity?: SyringeDriverRunScheduledActivityDto;
  performedActivity?: SyringeDriverRunActivityDto;
}
interface Props extends MedicationInformationProps {
  drug: HSDrug;
  isLate: boolean;
  hasBeenDosed: boolean;
  canReadminister: boolean;
}

//eslint-disable-next-line max-lines-per-function
export function SyringeDriverRunItems(props: Props) {
  const syringeDriverRun = props.syringeDriverRun;
  const services = useSyncCenter();
  const roundStore = useMap(services.rounds);
  const [open, setOpen] = useState(false);
  const [activityKind, setActivityKind] = useState<SyringeDriverRunActivityTypeDto>(SyringeDriverRunActivityTypeDto.Check);
  const [unscheduledActivity, setUnscheduledActivity] = useState<SyringeDriverRunActivityDto>({
    syringeDriverRunId: syringeDriverRun?.hsId, 
    activityType: SyringeDriverRunActivityTypeDto.Check
  });
  if (!syringeDriverRun) {
    return null;
  }
  
  const apiUtils = useApiUtils();
  const [activities, setActivities] = useState<SdrActivity[]>([]);
  // Get the master list of activities.  This is the union of scheduled and unscheduled.
  useEffect(() => {
    let result: SdrActivity[] = [];
    syringeDriverRun.activities?.forEach(act => {
      const newActivity: SdrActivity = {
        activityType: "Performed",
        activityTime: act.clientCreatedAt!,
        performedActivity: act
      };
      const scheduledActivity = syringeDriverRun.scheduledActivities?.find(x => x.currentActivityId === act.hsId || x.hsId === act.scheduledActivityId);
      if (scheduledActivity) {
        newActivity.scheduledActivity = scheduledActivity;
      }
      result.push(newActivity);
    });
    syringeDriverRun.scheduledActivities?.forEach(act => {
      // Only include it if it is not related to a scheduled activity.
      if (!result.some(x => x.scheduledActivity?.hsId === act.hsId)) {
        result.push({
          activityType: "Scheduled",
          scheduledActivity: act,
          activityTime:act.scheduledAt!
        });
      }
    });
    result.sort((a, b) => DateUtils.toDate(a.activityTime).getTime() - DateUtils.toDate(b.activityTime).getTime());
    setActivities(result);
  }, [syringeDriverRun]);
  

  const now = new Date();

  function openActivityDialog(kind: SyringeDriverRunActivityTypeDto) {
    if (!props.administrationPermissions.canOperateSyringeDriver) {
      toasts.error('You do not have permission to operate syringe driver');
    }
    else {
      setActivityKind(kind);
      setUnscheduledActivity({
        syringeDriverRunId: syringeDriverRun?.hsId,
        activityType: kind
      })
      setOpen(true);
    }
  }

  function CheckIfCanBeSighted(scheduledActivity?: SyringeDriverRunScheduledActivityDto) {
    if (!scheduledActivity) {
      return false;
    } 
    // You can always sight syringe drivers.
    let currentTime = props.currentRound?.createdAt ? DateUtils.toDate(props.currentRound.createdAt) : new Date();
    const roundWindow = apiUtils.rounds.getRoundWindow(currentTime, props.facilityGroupId);
    if (DateUtils.toDate(scheduledActivity.scheduledAt!).valueOf() < DateUtils.toDate(roundWindow.start).valueOf()) {
      return false;
    }
    if (DateUtils.toDate(scheduledActivity.scheduledAt!).valueOf() > DateUtils.toDate(roundWindow.end).valueOf()) {
      return false;
    }
    return true;
  }
  function CheckIfCanBeSightedLate(scheduledActivity?: SyringeDriverRunScheduledActivityDto)
  {
    if (!scheduledActivity || !props.canReadminister) {
      // Not allowed to readminister.
      return false;
    }
    return (DateUtils.toDate(scheduledActivity.scheduledAt!) >= startOfDay(new Date()));
  }
  function isFuture(scheduledActivity?: SyringeDriverRunScheduledActivityDto)
  {
    if (!scheduledActivity) {
      return false;
    }
    let currentTime = props.currentRound?.createdAt ? DateUtils.toDate(props.currentRound.createdAt) : new Date();
    const roundWindow = apiUtils.rounds.getRoundWindow(currentTime, props.facilityGroupId);
    return (DateUtils.toDate(scheduledActivity.scheduledAt!) > DateUtils.toDate(roundWindow.end));
  }

  function isInRound(scheduledActivity?: SyringeDriverRunScheduledActivityDto)
  {
    if (!scheduledActivity || !props.currentRound) {
      return false;
    }
    return props.roundSchedule?.some(x => x.syringeDriverRunActivity?.scheduledActivity === scheduledActivity);
  }

  function getFirstScheduleItem(activities: SdrActivity[]): SdrActivity[] {
    activities = activities.sort((a, b) => a.activityTime.localeCompare(b.activityTime));
    if (!!props.currentRound) {
      const roundActivity = activities.find(x => isInRound(x.scheduledActivity));
      if (!!roundActivity) {
        return [roundActivity];
      }
      return [];
    }
    // Get the next available activity that has not been performed.
    let result = activities.find(x => !!x.scheduledActivity && x.scheduledActivity.status === SyringeDriverRunScheduledActivityStatusDto.Pending && CheckIfCanBeSighted(x.scheduledActivity));
    const available_activities = activities.filter(x => !!x.performedActivity).sort((a, b) => a.activityTime.localeCompare(b.activityTime));
    
    if (!result && available_activities && available_activities.length > 0) {
      // Everything has been performed, so show the last one that was performed.
      result = available_activities[available_activities.length - 1];
    }
    if (result) {
      return [result];
    }
    return [];
  }
  // Don't display checks that are after the current window ends.
  function shouldBeDisplayed(scheduledActivity: SyringeDriverRunScheduledActivityDto | undefined)
  {
    if (!scheduledActivity) {
      // This is an ad-hoc activity - display it.
      return true;
    }
    if (scheduledActivity.status === SyringeDriverRunScheduledActivityStatusDto.Inactive) {
      return  false;
    }
    return (!isFuture(scheduledActivity) || scheduledActivity.activityType !== SyringeDriverRunActivityTypeDto.Check); 
  }
  
  function isLate(scheduledActivity?: SyringeDriverRunScheduledActivityDto): boolean {
    if (!scheduledActivity) {
      return false;
    }
    let currentTime = props.currentRound?.createdAt ? DateUtils.toDate(props.currentRound.createdAt) : new Date();
    const roundWindow = apiUtils.rounds.getRoundWindow(currentTime, props.facilityGroupId);
    // So it is either one that has been missed or a future dose.
    // If it is an activity for today, and is before the current time, then it must be one that can be dosed late.
    return (DateUtils.toDate(scheduledActivity.scheduledAt!) < DateUtils.toDate(roundWindow.start));
  }
  const theme = useTheme();

  const isAlreadyFinished = syringeDriverRun.status === SyringeDriverRunStatusDto.Stopped;
  function updatedProps(original: Props, activity?: SyringeDriverRunScheduledActivityDto): Props {
    return {...original, isLate: isLate(activity)};
  }
  return (
      <>
        <StatusBox>
          <Text weight={'bold'}>Current Status as at {DateUtils.toDisplayString(syringeDriverRun.clientUpdatedAt)}</Text>
          <ActivityBox>
            <Layout>
              
              <NameValue name="Status">
                <span  style={{ color: "#B040F9" }}>SYRINGE DRIVER {syringeDriverRun.syringeDriverIdentifier}</span> is {syringeDriverRun.status}                
              </NameValue>
              <NameValue name="Time">
                {DateUtils.toDisplayString(syringeDriverRun.startedAt)} - {DateUtils.toDisplayString(syringeDriverRun.scheduledEnd!)}
              </NameValue>
              <NameValue name="Applied to">
                {syringeDriverRun.site?.position}
                  <SiteCondition value={syringeDriverRun.site?.condition}/>
              </NameValue>
              {
                  syringeDriverRun.line?.condition &&
                  <NameValue name="Line condition">
                    <LineCondition value={syringeDriverRun.line?.condition}/>
                  </NameValue>
              }
              <NameValue name="Amount" bold={false}>
                  Administered <Text weight="bold">{syringeDriverRun.infusion?.amountAdministeredMl}ml</Text> of <Text weight="bold">{syringeDriverRun.infusion?.amountTotalMl}ml</Text> at a rate of <Text weight="bold">{syringeDriverRun.infusion?.rateMlPerH}ml/hr</Text> 
                &nbsp;diluted in <Text weight="bold">{syringeDriverRun.diluent}</Text>
              </NameValue>
              {
                  syringeDriverRun.statusCheck?.batteryPercentage &&
                  <NameValue name="Battery">
                    <BatteryLevel value={syringeDriverRun.statusCheck?.batteryPercentage} />
                  </NameValue>
              }
              {
                  syringeDriverRun.comment &&
                  <NameValue name="Notes">
                    {syringeDriverRun.comment}
                  </NameValue>
              }
              {
                syringeDriverRun.status !== SyringeDriverRunStatusDto.Stopped && 
                <ActionDiv>
                  { syringeDriverRun.status === SyringeDriverRunStatusDto.Paused && new Date() < DateUtils.toDate(syringeDriverRun.scheduledEnd!) && 
                      <ActionBox>
                        <ActionButton onClick={() => openActivityDialog(SyringeDriverRunActivityTypeDto.Restart)}>RESUME</ActionButton>
                      </ActionBox>
                  }
                  { syringeDriverRun.status === SyringeDriverRunStatusDto.Running &&
                      <>
                      { props.displayAllActivity && new Date() < DateUtils.toDate(syringeDriverRun.scheduledEnd!) &&
                        <ActionBox>
                          <ActionButton onClick={() => openActivityDialog(SyringeDriverRunActivityTypeDto.Check)}>OBSERVE NOW</ActionButton>
                        </ActionBox>
                      }
                        <ActionBox>
                          <ActionButton onClick={() => openActivityDialog(SyringeDriverRunActivityTypeDto.Stop)}>STOP / PAUSE</ActionButton>
                        </ActionBox>
                      </>
                  }
                </ActionDiv>
              }
           </Layout>
          </ActivityBox>
          
        </StatusBox>
        {
          // Only show ScheduledItemsBox if there are activities to display
          (() => {
            const filteredActivities = props.displayAllActivity ? activities?.filter(x => shouldBeDisplayed(x.scheduledActivity)) : getFirstScheduleItem(activities);
            const hasDisplayableActivities = filteredActivities?.some((activity, index) => props.displayAllActivity || index === 0);
            const hasResumeCard = props.administrationPermissions.canOperateSyringeDriver && syringeDriverRun.status === SyringeDriverRunStatusDto.Paused;
            

            if (!hasDisplayableActivities && !hasResumeCard) {
                return null;
            }

            return (
                <ScheduledItemsBox>
                  <Layout gap={0.8}>
                    <Text weight={'bold'}>Activity</Text>
                    {
                      filteredActivities?.map((activity, index) => {
                        if (props.displayAllActivity || index === 0) {
                          return <SyringeDriverRunActivityCard
                              syringeDriverRun={syringeDriverRun} {...updatedProps(props, activity.scheduledActivity)}
                              {...props}
                              performedActivity={activity.performedActivity}
                              scheduledActivity={activity.scheduledActivity}
                              canBeSighted={!isAlreadyFinished && CheckIfCanBeSighted(activity.scheduledActivity)}
                              canBeSightedLate={!isAlreadyFinished && CheckIfCanBeSightedLate(activity.scheduledActivity)}
                              isFuture={isFuture(activity.scheduledActivity)}
                              key={index}></SyringeDriverRunActivityCard>
                        }
                        return null;
                      })
                    }

                    {
                      // Display the resume.
                      hasResumeCard &&
                      <SyringeDriverRunActivityCard
                          syringeDriverRun={syringeDriverRun}
                          {...props}
                          canBeSighted={false}
                          canBeSightedLate={false}
                          isFuture={false}
                          unscheduledActivity={{
                            activityType: SyringeDriverRunActivityTypeDto.Restart,
                            syringeDriverRunId: syringeDriverRun.hsId,
                            syringeDriverRunClinicalSystemId: syringeDriverRun.clinicalSystemId,
                          }}
                          key={-1}></SyringeDriverRunActivityCard>
                    }
                  </Layout>
                </ScheduledItemsBox>
            );
          })()
        }
        <SyringeDriverRunActivityDialog
          syringeDriverRun={syringeDriverRun}
        setOpen={setOpen}
        open={open}
        activityKind={activityKind}
        unscheduledActivity={unscheduledActivity}
        {...props}
      />              
    </>
  );
}
interface NameValueProps {
  name: string;
  children: React.ReactNode;
  bold?: boolean;
}
function NameValue(props: NameValueProps) {
  let style= {};
  let bold = true;
  if (props.bold === undefined) {
    bold = true;
  }
  else if (props.bold === false) {
    bold = false;
  }
  if (bold) {
    style = { fontWeight: "bold"};
  }
  return <>
    <span>{props.name}</span>
    <ValueDiv>
      <span style={style}>{props.children}</span>
    </ValueDiv>
  </>
}
interface BatteryLevelProps {
  value: number;
}
function BatteryLevel(props: BatteryLevelProps) {
  let style= {};
  if (props.value <= 50) {
    style = {
      color: "red"
    };
  } 
  return (<span style={style}>{props.value}%</span>);
}
interface ConditionProps {
  value: string | undefined | null;
}
function SiteCondition(props: ConditionProps) {
  let style= {};
  if (!props.value) {
    return null;
  }
  const lcText = props.value.toLowerCase();
  if (lcText.includes("red") || lcText.includes("inflame") || lcText.includes("leak")) {
    style = {
      color: "red"
    };
  }
  return (<>&nbsp;-&nbsp;<span style={style}>{props.value}</span></>);
}
function LineCondition(props: ConditionProps) {
  let style= {};
  if (!props.value) {
    return null;
  }
  const lcText = props.value.toLowerCase();
  if (lcText.includes("cloudy")) {
    style = {
      color: "red"
    };
  }
  return (<span style={style}>{props.value}</span>);
}
interface SyringeDriverRunActivityCardProps extends Props {
  drug: HSDrug;
  syringeDriverRun: SyringeDriverRunDto;
  performedActivity?: SyringeDriverRunActivityDto;
  scheduledActivity?: SyringeDriverRunScheduledActivityDto;
  unscheduledActivity?: SyringeDriverRunActivityDto;
  canBeSighted: boolean;
  canBeSightedLate: boolean;
  isLate: boolean;
  isFuture: boolean;
  hasBeenDosed: boolean;
}

// eslint-disable-next-line sonarjs/cognitive-complexity, max-lines-per-function
function SyringeDriverRunActivityCard(props: SyringeDriverRunActivityCardProps) {
  const userUtils = useApiUtils().users;
  const [open, setOpen] = useState(false);
  const [activityKind, setActivityKind] = useState<SyringeDriverRunActivityTypeDto>(SyringeDriverRunActivityTypeDto.Check);
  function openActivityDialog(kind: SyringeDriverRunActivityTypeDto) {
    if (!props.administrationPermissions.canOperateSyringeDriver) {
      toasts.error('You do not have permission to operate syringe driver');
    } 
    else {
      setActivityKind(kind);
      setOpen(true);
    }
  }
  const activityType = props.performedActivity?.activityType ?? props.scheduledActivity?.activityType;
  const activityTime = props.performedActivity?.clientCreatedAt ?? props.scheduledActivity?.scheduledAt ?? (new Date()).toDateString();

  //eslint-disable-next-line sonarjs/cognitive-complexity
  function getDescription() {

    switch (activityType) {
    case SyringeDriverRunActivityTypeDto.Check:
        if (props.performedActivity) {
            console.log("perfAct: ", props.performedActivity);
        }
      return props.performedActivity ? `Observed at ${DateUtils.toDisplayString(DateUtils.toDate(activityTime))} by ${userUtils.getUserName(props.performedActivity?.clientUser)}.` : `No observation added.`;
    case SyringeDriverRunActivityTypeDto.Pause:
      return `Paused at ${DateUtils.toDisplayString(DateUtils.toDate(activityTime))} by ${userUtils.getUserName(props.performedActivity?.clientUser)}.`;
    case  SyringeDriverRunActivityTypeDto.Start:
      return `Syringe driver started by ${userUtils.getUserName(props.performedActivity?.clientUser)}`;
    case  SyringeDriverRunActivityTypeDto.Restart:
      return `Syringe driver re-started by ${userUtils.getUserName(props.performedActivity?.clientUser)}`;
    case  SyringeDriverRunActivityTypeDto.Cease:
      return props.performedActivity ? `Syringe driver ceased at ${DateUtils.toDisplayString(props.performedActivity.clientCreatedAt)} by ${userUtils.getUserName(props.performedActivity?.clientUser)}`
          : `Syringe driver course due for completion.`;
    case  SyringeDriverRunActivityTypeDto.Stop:
      return props.performedActivity ? `Syringe driver stopped at ${DateUtils.toDisplayString(props.performedActivity.clientCreatedAt)} by ${userUtils.getUserName(props.performedActivity?.clientUser)}`
          : `Syringe driver course due for completion.`;
    default:
      return "";
    }
  }
  return (
    <ActivityBox>
      <Layout>
        <Text weight={'bold'}>
          {DateUtils.toDisplayString(activityTime)}
          {!!props.scheduledActivity && !!props.performedActivity &&
              <i>&nbsp;(scheduled at {DateUtils.toDisplayString(DateUtils.toDate(props.scheduledActivity.scheduledAt!))})</i>
          }
        </Text>
        <Text>{getDescription()}</Text>
      </Layout>
      {!!props.scheduledActivity && !props.performedActivity && (
          <>
            { !props.canBeSighted && !props.isFuture && <MissedBox> MISSED </MissedBox> }
            
            { props.canBeSighted && props.scheduledActivity.activityType === SyringeDriverRunActivityTypeDto.Check && props.scheduledActivity.status === SyringeDriverRunScheduledActivityStatusDto.Performed &&
                <ActionButton onClick={() => openActivityDialog(SyringeDriverRunActivityTypeDto.Check)}>VIEW</ActionButton>
            }
            { props.canBeSighted && props.scheduledActivity.activityType === SyringeDriverRunActivityTypeDto.Check && props.scheduledActivity.status === SyringeDriverRunScheduledActivityStatusDto.Pending &&
                <ActionButton onClick={() => openActivityDialog(SyringeDriverRunActivityTypeDto.Check)}>OBSERVE</ActionButton>
            }
            { (props.scheduledActivity.activityType === SyringeDriverRunActivityTypeDto.Cease ||
                props.scheduledActivity.activityType === SyringeDriverRunActivityTypeDto.Stop) &&
                <ActionButton onClick={() => openActivityDialog(SyringeDriverRunActivityTypeDto.Stop)}>STOP</ActionButton>
            }
          </>
      )}
      {!!props.performedActivity && (
          <>
            <ActionButton onClick={() => openActivityDialog(SyringeDriverRunActivityTypeDto.Check)}>VIEW</ActionButton>
          </>
      )}
      <SyringeDriverRunActivityDialog
          setOpen={setOpen}
          open={open}
          activityKind={activityKind}
          performedActivity={props.performedActivity}
          scheduledActivity={props.scheduledActivity}
          {...props}
      />
      
    </ActivityBox>
  );
}

/*
function UnscheduledObservationCard(props: ActivityProps) {
  const [open, setOpen] = useState(false);

  function openActivityDialog() {
    if (!props.administrationPermissions.canOperateSyringeDriver) {
      toasts.error('You do not have permission to operate syringe driver');
    } else {
      setOpen(true);
    }
  }

  return (
    <ActivityBox style={{ background: 'white', borderStyle: 'none' }}>
      <Layout>
        <Text weight={'bold'}>
          {DateUtils.dateTo24HourTimeString(new Date())} ON {format(new Date(), 'd MMM y')}
        </Text>
        <Text>Add an observation now.</Text>
      </Layout>
      <ActionButton onClick={openActivityDialog}>OBSERVE NOW</ActionButton>
      <SyringeDriverActivityDialog
        {...props}
        setOpen={setOpen}
        open={open}
        activityKind={SyringeDriverActivityKind.Observation}
      />
    </ActivityBox>
  );
}

export function isSyringeDriverActivity(
  activity: MedisphereSyringeDriverActivity | HSPatchObservation |  MedisphereSyringeDriverRunActivity,
): activity is MedisphereSyringeDriverActivity {
  return 'rateOfInfusion' in activity;
}
export function isSyringeDriverRunActivity(
    activity: MedisphereSyringeDriverActivity | HSPatchObservation |  MedisphereSyringeDriverRunActivity,
): activity is  MedisphereSyringeDriverRunActivity {
  return 'syringeDriverRunId' in activity;
}
*/
const ActivityBox = styled.div`
  border-style: solid;
  border-color: #c5bacb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
`;

const ActionButton = styled(Button)`
  min-width: 10rem;
`;
const MissedBox = styled.div`
  min-width: 10rem;
  text-align: center;
`;

const ScheduledItemsBox = styled.div`
  background-color: rgba(246, 233, 254, 1);
  padding: 1.25rem;

  flex-direction: column;
  display: flex;
  width: 100%;
`;
const StatusBox = styled.div`
  background-color: rgba(246, 233, 254, 1);
  padding: 1.25rem;

  flex-direction: column;
  display: flex;
  width: 100%;
  
`;
const ActionDiv = styled.div`
  display: flex;
`
const ActionBox = styled.div`
  width: 15em;
  padding: 0.25rem;
`
const ValueDiv = styled.div`
  padding-bottom: 1em;
`;
